/**
 * Utilitaires de transformation des événements stream en timeline journalière.
 * @module pages/utils/streamStatsEvolution
 */

const SUB_EVENT_TYPES = new Set(['sub', 'resub', 'giftsub']);

/**
 * Retourne la date à minuit (heure locale)
 * @param {Date} date
 * @returns {Date}
 */
function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Convertit une date en clé stable YYYY-MM-DD (heure locale)
 * @param {Date} date
 * @returns {string}
 */
function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Construit une timeline journalière à partir d'événements Twitch.
 * @param {Array<{timestamp: string, event_type: string}>} events
 * @param {number} days
 * @param {Date} [now]
 * @returns {Array<{dateKey: string, label: string, follows: number, unfollows: number, subs: number, totalEvents: number, netFollowers: number}>}
 */
export function buildDailyEvolution(events = [], days = 7, now = new Date()) {
  const safeDays = Number.isInteger(days) && days > 0 ? days : 7;
  const today = startOfDay(now);
  const firstDay = new Date(today);
  firstDay.setDate(today.getDate() - (safeDays - 1));

  const timeline = [];
  const bucketByDateKey = new Map();

  for (let index = 0; index < safeDays; index += 1) {
    const currentDay = new Date(firstDay);
    currentDay.setDate(firstDay.getDate() + index);
    const dateKey = toDateKey(currentDay);
    const bucket = {
      dateKey,
      label: currentDay.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
      }),
      follows: 0,
      unfollows: 0,
      subs: 0,
      totalEvents: 0,
      netFollowers: 0,
    };
    bucketByDateKey.set(dateKey, bucket);
    timeline.push(bucket);
  }

  for (const event of events) {
    if (!event?.timestamp || !event?.event_type) {
      continue;
    }

    const eventDate = new Date(event.timestamp);
    if (Number.isNaN(eventDate.getTime())) {
      continue;
    }

    const dateKey = toDateKey(eventDate);
    const bucket = bucketByDateKey.get(dateKey);
    if (!bucket) {
      continue;
    }

    if (event.event_type === 'follow') {
      bucket.follows += 1;
    } else if (event.event_type === 'unfollow') {
      bucket.unfollows += 1;
    } else if (SUB_EVENT_TYPES.has(event.event_type)) {
      bucket.subs += 1;
    } else {
      continue;
    }
  }

  return timeline.map((bucket) => {
    const totalEvents = bucket.follows + bucket.unfollows + bucket.subs;
    return {
      ...bucket,
      totalEvents,
      netFollowers: bucket.follows - bucket.unfollows,
    };
  });
}
